'use client';

/**
 * SectionNav.tsx — fast seksjonsmeny for produktside (ibm.com/products)
 *
 * Ligger under hero, og festes under headeren (3rem) når man har scrollet
 * forbi. Markerer seksjonen som er i visning og viser en blå→lilla
 * fremdriftslinje. Primærhandling til høyre.
 *
 * Festingen bruker position: fixed styrt av scroll-posisjon, ikke
 * position: sticky — html/body har overflow i de globale stilene, som
 * ødelegger sticky.
 */

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import type { ProductSectionNav } from '@/types/product';
import styles from './section-nav.module.css';

/** Høyde på fast header (3rem) og på selve menyen (5rem), i px */
const HEADER_PX = 48;
const NAV_PX = 80;

export default function SectionNav({ nav }: { nav: ProductSectionNav }) {
  const slot = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  const [active, setActive] = useState(nav.items[0]?.id ?? '');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frame = 0;

    const update = () => {
      frame = 0;
      const top = slot.current?.getBoundingClientRect().top ?? 1;
      setStuck(top <= HEADER_PX);

      // Aktiv seksjon: den siste hvis topp har passert menyens underkant
      const line = HEADER_PX + NAV_PX + 8;
      let current = nav.items[0]?.id ?? '';
      for (const item of nav.items) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= line) current = item.id;
      }
      setActive(current);

      // Fremdrift gjennom seksjonene (første seksjon → siste seksjon)
      const first = document.getElementById(nav.items[0]?.id ?? '');
      const last = document.getElementById(nav.items[nav.items.length - 1]?.id ?? '');
      if (first && last) {
        const start = first.getBoundingClientRect().top + window.scrollY - line;
        const end = last.getBoundingClientRect().top + window.scrollY - line;
        const pct = end > start ? (window.scrollY - start) / (end - start) : 0;
        setProgress(Math.min(1, Math.max(0, pct)));
      }
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [nav.items]);

  return (
    // slot holder av plassen når menyen er festet (unngår hopp i innholdet)
    <div ref={slot} className={styles.slot}>
      <nav className={`${styles.nav} ${stuck ? styles.stuck : ''}`} aria-label={nav.label}>
        {stuck && (
          <div className={styles.progress} aria-hidden>
            <div className={styles.progressBar} style={{ transform: `scaleX(${progress})` }} />
          </div>
        )}
        <div className={styles.inner}>
          <ul className={styles.list} role="list">
            {nav.items.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`${styles.link} ${active === item.id ? styles.active : ''}`}
                  aria-current={active === item.id ? 'location' : undefined}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
          {nav.cta.href.startsWith('#') ? (
            <a href={nav.cta.href} className={styles.cta}>
              {nav.cta.label}
            </a>
          ) : (
            <Link href={nav.cta.href} className={styles.cta}>
              {nav.cta.label}
            </Link>
          )}
        </div>
      </nav>
    </div>
  );
}
